import type { AssistantMessage, Message, Model, ModelRef, Tokens } from "./opencodeTypes"

// How full the conversation is.
//
// A port of the agent's own answer, at
// agent/packages/app/src/components/session/session-context-metrics.ts, and it
// is worth keeping the two in step: the desktop app, the TUI's sidebar and its
// composer footer all compute this the same way, and a browser chat that
// disagreed would be reporting a different number for the same session.
//
// Two rules here are counter-intuitive enough to be worth stating, because
// getting either wrong produces a plausible number rather than an obvious bug.

/** input + output + reasoning + both cache halves. */
export function tokenTotal(tokens: Tokens): number {
  return tokens.input + tokens.output + tokens.reasoning + tokens.cache.read + tokens.cache.write
}

/**
 * The last assistant message that has token counts.
 *
 * Rule one: this is a *snapshot*, not a sum. A turn's `tokens.input` already
 * covers the whole conversation as it was replayed to the model, so adding
 * turns together counts the same history once per turn and produces a figure
 * several times the real one. Reading only the most recent turn is what makes
 * the percentage fall after a compaction rather than climbing forever.
 *
 * Skipping zero-token messages matters for the turn in flight: an assistant
 * message exists from `step.started`, but `tokens` only arrives at
 * `step.ended`, so without this the meter would drop to nothing every time the
 * model began to answer.
 */
function lastMeasured(messages: Message[]): AssistantMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index]
    if (message?.type !== "assistant") continue
    if (!message.tokens || tokenTotal(message.tokens) <= 0) continue
    return message
  }
  return undefined
}

export interface SessionContext {
  message: AssistantMessage
  model?: Model
  modelLabel: string
  /** The model's context window, or undefined when nobody has filled it in. */
  limit?: number
  tokens: Tokens
  total: number
  /** Percent of the window in use, or null when the limit is unknown. */
  usage: number | null
}

/**
 * Rule two: `usage` is null rather than 0 when the limit is unknown.
 *
 * A model whose `limit.context` is 0 has not had its window looked up — the
 * workspace container omits `limit` when the gateway did not report one. "0% of
 * an unknown window" and "0% used" are different claims, and only one of them
 * is true. Callers render null as an em dash.
 */
export function sessionContext(
  messages: Message[],
  models: Model[] = [],
  fallbackModel?: ModelRef,
): SessionContext | undefined {
  const message = lastMeasured(messages)
  if (!message?.tokens) return undefined

  // The ref on the message, not the session's current one: this describes the
  // turn that produced these counts, and a model switched since then has a
  // different window.
  const ref = message.model ?? fallbackModel
  const model = ref
    ? models.find((item) => item.id === ref.id && item.providerID === ref.providerID)
    : undefined

  const limit = model?.limit?.context || undefined
  const total = tokenTotal(message.tokens)

  return {
    message,
    model,
    modelLabel: model?.name ?? ref?.id ?? "Unknown model",
    limit,
    tokens: message.tokens,
    total,
    usage: limit ? Math.round((total / limit) * 100) : null,
  }
}
